import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Card, CardContent, Grid, Stack, styled, Typography } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router';
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import ErrorBoundary from '../../Common/_components/ErrorBoundary';
import Dialog from '../../Common/Dialog/Dialog';
import DialogActions from '../../Common/Dialog/DialogActions';
import DialogButton from '../../Common/Buttons/DialogButton';
import DialogContent from '../../Common/Dialog/DialogContent';
import DialogTitle from '../../Common/Dialog/DialogTitle';
import ProductCatalogCardImage, { hasProductCatalogImageSet } from '../../ProductCatalog/Card/ProductCatalogCardImage';
import { FetchUserStorePresets } from '../../UserData/_actions/Store/UserStorePresetActions';
import useProductCatalogCategories from '../../ProductCatalog/hooks/useProductCatalogCategories';
import EditProduct from './AddProductListing/EditProduct';
import StoreHeader from "./StoreHeader";

const CustomizedAccordion = styled(Accordion)({
	'&.MuiPaper-elevation1': {
		boxShadow: 'none',
		margin: 0,
	},

	'.MuiAccordionSummary-root': {
		padding: 'unset',
	},

	'&.MuiAccordion-root.Mui-expanded:before': {
		opacity: 1,
	}
});
const CustomizedAccordionSummary = styled(AccordionSummary)({
	'&.MuiAccordionSummary-root.Mui-expanded': {
		minHeight: '48px',
	},

	'.MuiAccordionSummary-content.Mui-expanded': {
		margin: '12px 0',
	}
});
const ParentCircle = styled('div')({
	width: '144px',
	height: '144px',
	margin: 'auto',
	padding: '10px',
	borderRadius: '50%',
	boxSizing: 'border-box',
	transition: 'background-color 150ms',
	cursor: 'pointer',

	'&:hover': {
		backgroundColor: '#FCEFF7',
	},

	'&:hover .eButton': {
		opacity: '1',
	},

	'&:focus': {
		border: '1px solid #DA3192',
	},

	'&.not-configured img': {
		opacity: '0.5',
	},

	'&.not-configured:hover img': {
		opacity: '1',
	}
});
const ChildCircle = styled('div')({
	borderRadius: '50%',
	backgroundColor: '#FFFFFF',
	border: '1px solid #DA3192',
	textAlign: 'center',
	height: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	position: 'relative',

	'.eButton': {
		display: 'flex',
		opacity: '0',
		transition: 'opacity 150ms',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '5px',
		position: 'absolute',
		top: '45px',
		left: '30px',
		minHeight: '30px',
		minWidth: '70px',
		color: '#333333',
		backgroundColor: '#ffffff',
		border: '1px solid #333333',
		borderRadius: '20px',
	}
});
const ProductImage = styled('div')({
	width: '100%',
	height: '100%',
	borderRadius: '50%',
	overflow: 'hidden',
	position: 'relative',
	transition: 'opacity 150ms',

	'.product-catalog-card-hover-image': {
		display: 'none',
	},
});

const AddButton = styled('div')({
	position: 'absolute',
	left: '50%',
	marginLeft: '-14px',
	width: '28px',
	height: '28px',
	top: '50%',
	marginTop: '-14px',
	lineHeight: '28px',
	backgroundColor: '#DA3192',
	borderRadius: '50%',

	'.material-icons': {
		color: '#fff',
		verticalAlign: 'middle',
		fontSize: '20px',
	}
});
const TextCenter = styled('div')({
	textAlign: 'center',
});

export default function StorePresets() {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')

  const params = useParams<{ storeSlug: string }>()

  const { categoriesById, topLevelCategories } = useProductCatalogCategories()
  const products = useAppSelector(state => state.get('appData').get('products').filter(product => product.get('visible')), (a, b) => a === b || a.equals(b))
  const viewProducts = useAppSelector(state => state.get('appData').get('viewProducts'), (a, b) => a === b || a.equals(b))
  const storePresets = useAppSelector(state => state.get('userData').get('storePresets'))

  const productGroups = useMemo(() => topLevelCategories.map(category => ({
    category,
    products: products.filter(product => product.get('viewCategories').some(categoryId => {
      return categoryId === category.get('id')
        || categoriesById.get(categoryId)?.get('id_parent') === category.get('id');
    })),
  })).filter(group => group.products.count() > 0), [categoriesById, products, topLevelCategories])

  const [dialogOpened, setDialogOpened] = useState(false)
  const [expanded, setExpanded] = useState<number | undefined>(undefined)
  const [closeDialogOpened, setCloseDialogOpened] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(0)

  useEffect(() => {
    dispatch(FetchUserStorePresets())
  }, [dispatch])

  const handleChangeAccordion = useCallback((panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    document.getElementById(`topOfPresets`)?.scrollIntoView()
    setExpanded(isExpanded ? panel : undefined)
  }, [])

  const handleCloseModal = useCallback(() => {
    setCloseDialogOpened(false)
    setDialogOpened(false)
  }, [])

  const onCloseDialogOpen = useCallback(() => setCloseDialogOpened(true), [])
  const onCloseDialogClose = useCallback(() => setCloseDialogOpened(false), [])

  const onDialogOpen = useCallback(() => setDialogOpened(true), [])
  const onDialogClose = useCallback(() => setDialogOpened(false), [])
  const onProductOpen = useCallback((productId: number) => {
    onDialogOpen();
    setSelectedProduct(productId)
  }, [onDialogOpen])
  const handleStep = useCallback(() => undefined, [])

  if (!params.storeSlug) return null;

  return <div>
    <StoreHeader
      title={t('Presets')}
    />

    <Stack spacing={4}>
      <Card elevation={0}>
        <CardContent>
          <div>
            <Stack spacing={1}>
              <Typography variant="h4">{t('Product Presets')}</Typography>
              <Typography variant="body2">{t('Create default pricing and listing details for the various product types, to be used as default when you publish them to your stores.')}</Typography>
            </Stack>

            <div>
              <div>
                {productGroups.map(({ category, products: filteredProducts }) => {
                  const nbProductsConfigured = filteredProducts.filter((product) => {
                    return storePresets.has(product.get('id')) && storePresets.getIn([product.get('id'), 'default']) !== true
                  }).count();

                  return <CustomizedAccordion
                    key={category.get('id')}
                    id={category.get('id').toString()}
                    square
                    expanded={expanded === category.get('id')} onChange={handleChangeAccordion(category.get('id'))}
                  >
                    <CustomizedAccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h5">{category.get('name')}</Typography>
                        <Typography variant="caption">{t('({{count}} / {{total}} configured)', { count: nbProductsConfigured, total: filteredProducts.count() })}</Typography>
                      </Stack>
                    </CustomizedAccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1} >
                        {filteredProducts.entrySeq().map(([productId, product]) => {
                            const isConfigured = storePresets.has(product.get('id')) && storePresets.getIn([product.get('id'), 'default']) !== true;
                            const hasProductCatalogImage = hasProductCatalogImageSet(product.get('slug'));
                            const catalogImageProduct = viewProducts.find(viewProduct => {
                              return viewProduct.get('id_product') === product.get('id')
                                && hasProductCatalogImageSet(viewProduct.get('slug'));
                            });
                            const catalogImageSlug = hasProductCatalogImage ? product.get('slug') : catalogImageProduct?.get('slug');
                            const catalogImageName = hasProductCatalogImage ? product.get('name') : catalogImageProduct?.get('name') ?? product.get('name');
                            return <Grid key={productId} item xs={6} md={4} lg={3}>
                              <div onClick={() => onProductOpen(product.get('id'))}>
                                <ParentCircle className={!isConfigured ? 'not-configured' : ''}>
                                  <ChildCircle>
                                    {catalogImageSlug !== undefined ? <ProductImage>
                                      <ErrorBoundary fallback={<></>}>
                                        <ProductCatalogCardImage
                                          slug={catalogImageSlug}
                                          name={catalogImageName}
                                          sizes="144px"
                                        />
                                      </ErrorBoundary>
                                    </ProductImage> : null}
                                    {isConfigured ? <div className='eButton'>
                                      <div>
                                        <svg width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M9.83594 8.2207C9.78906 8.26758 9.78906 8.31445 9.78906 8.36133V11.0098H1.53906V2.75977H7.1875C7.23438 2.75977 7.28125 2.75977 7.32812 2.71289L8.07812 1.96289C8.19531 1.8457 8.10156 1.63477 7.9375 1.63477H1.53906C0.90625 1.63477 0.414062 2.15039 0.414062 2.75977V11.0098C0.414062 11.6426 0.90625 12.1348 1.53906 12.1348H9.78906C10.3984 12.1348 10.9141 11.6426 10.9141 11.0098V7.61133C10.9141 7.44727 10.7031 7.35352 10.5859 7.4707L9.83594 8.2207ZM13.4922 3.50977C14.0312 2.9707 14.0312 2.10352 13.4922 1.56445L12.4844 0.556641C11.9453 0.0175781 11.0781 0.0175781 10.5391 0.556641L4.39844 6.69727L4.16406 8.83008C4.09375 9.43945 4.60938 9.95508 5.21875 9.88477L7.35156 9.65039L13.4922 3.50977ZM11.1953 4.21289L6.83594 8.57227L5.28906 8.75977L5.47656 7.21289L9.83594 2.85352L11.1953 4.21289ZM12.6953 2.36133C12.8125 2.45508 12.8125 2.5957 12.7188 2.71289L11.9922 3.43945L10.6328 2.05664L11.3359 1.35352C11.4297 1.23633 11.5938 1.23633 11.6875 1.35352L12.6953 2.36133Z" fill="#333333" />
                                        </svg>
                                      </div>
                                      <div>Edit</div>
                                    </div> : <AddButton><i className="material-icons">add</i></AddButton>}
                                  </ChildCircle>
                                </ParentCircle>
                                <TextCenter>
                                  {product.get('name')}
                                </TextCenter>
                              </div>
                            </Grid>
                          })}
                      </Grid>
                    </AccordionDetails>
                  </CustomizedAccordion>
                })}
              </div>
            </div>
          </div>

          <Dialog
            open={dialogOpened}
            maxWidth="lg"
            fullWidth
            onClose={onCloseDialogOpen}
            showCloseButton
            hasTabs
          >
            <EditProduct
              store={params.storeSlug}
              index={0}
              handleStep={handleStep}
              onDialogClose={onDialogClose}
              onDialogCloseWithConfirm={onCloseDialogOpen}
              storePreset={storePresets.get(selectedProduct)}
              product={products.get(String(selectedProduct))}
              massPresets
            />
          </Dialog>

          <Dialog
            open={closeDialogOpened}
            maxWidth="xs"
            fullWidth
            onClose={onCloseDialogClose}
            showCloseButton
          >
            <DialogTitle>{t('Are you sure?')}</DialogTitle>
            <DialogContent>
              <p>{t('You will lose your changes if you close this dialog.')}</p>
            </DialogContent>
            <DialogActions>
              <DialogButton
                color="secondary"
                onClick={onCloseDialogClose}
              >{t('Cancel')}</DialogButton>
              <DialogButton
                color="primary"
                onClick={handleCloseModal}
                autoFocus
              >{t('Close')}</DialogButton>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    </Stack>
  </div>
}
